import { create } from "zustand";
import * as z from "zod";

export const CUSTOMER_ACCOUNT_IDS_STORAGE_KEY = "dxp-customer-account-ids";

const customerAccountPairSchema = z.object({
  cdhId: z.string(),
  iamId: z.string(),
});

export type CustomerAccountPair = z.infer<typeof customerAccountPairSchema>;

/**
 * Customer URLs carry the IAM id while GraphQL variables still take the CDH id.
 * Pairs persist per tab so a refresh resolves without refetching the customer list.
 */
type CustomerAccountIdsStore = {
  cdhIdByIamId: ReadonlyMap<string, string>;
  iamIdByCdhId: ReadonlyMap<string, string>;
  addCustomerAccountIds: (pairs: readonly CustomerAccountPair[]) => void;
};

const readStoredPairs = (): CustomerAccountPair[] => {
  try {
    const customerAccountIds = sessionStorage.getItem(
      CUSTOMER_ACCOUNT_IDS_STORAGE_KEY
    );
    if (customerAccountIds === null) {
      return [];
    }
    return z
      .array(customerAccountPairSchema)
      .parse(JSON.parse(customerAccountIds));
  } catch {
    // corrupt JSON or blocked storage — the route resolver refetches the pairs
    return [];
  }
};

const writeStoredPairs = (cdhIdByIamId: ReadonlyMap<string, string>): void => {
  try {
    sessionStorage.setItem(
      CUSTOMER_ACCOUNT_IDS_STORAGE_KEY,
      JSON.stringify(
        Array.from(cdhIdByIamId, ([iamId, cdhId]) => ({ cdhId, iamId }))
      )
    );
  } catch {
    // private mode / blocked storage — in-memory only
  }
};

const toMaps = (pairs: readonly CustomerAccountPair[]) => ({
  cdhIdByIamId: new Map(pairs.map(({ cdhId, iamId }) => [iamId, cdhId])),
  iamIdByCdhId: new Map(pairs.map(({ cdhId, iamId }) => [cdhId, iamId])),
});

export const useCustomerAccountIdsStore = create<CustomerAccountIdsStore>(
  (set) => ({
    ...toMaps(readStoredPairs()),
    addCustomerAccountIds: (pairs) => {
      set((state) => {
        const validPairs = pairs.filter(
          ({ cdhId, iamId }) => cdhId.length > 0 && iamId.length > 0
        );
        const hasChanges = validPairs.some(
          ({ cdhId, iamId }) => state.cdhIdByIamId.get(iamId) !== cdhId
        );
        if (!hasChanges) {
          return state;
        }

        const cdhIdByIamId = new Map(state.cdhIdByIamId);
        const iamIdByCdhId = new Map(state.iamIdByCdhId);
        for (const { cdhId, iamId } of validPairs) {
          cdhIdByIamId.set(iamId, cdhId);
          iamIdByCdhId.set(cdhId, iamId);
        }
        writeStoredPairs(cdhIdByIamId);
        return { cdhIdByIamId, iamIdByCdhId };
      });
    },
  })
);

export const resetCustomerAccountIdsStore = (): void => {
  useCustomerAccountIdsStore.setState(toMaps(readStoredPairs()));
};
